import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Spring from '@/components/Spring';
import LeaderboardHeader from '@/components/LeaderboardHeader';
import LeaderboardRow, { LeaderboardRowData } from '@/components/LeaderboardRow';
import styles from './styles.module.scss';
import classNames from 'classnames';

export interface LeaderboardEntry {
  id?: number;
  user_id?: number;
  username?: string;
  first_name?: string;
  last_name?: string;
  name?: string;
  total_points?: number;
  pts?: number;
  position?: number;
  color?: string;
  w?: number;
  d?: number;
  l?: number;
}

interface LeaderboardWidgetProps {
  title?: ReactNode;
  subtitle?: string;
  img?: string;
  entries?: LeaderboardEntry[];
  maxItems?: number;
  variant?: 'minimal' | 'league';
  linkTo?: string;
  className?: string;
}

const DEFAULT_COLORS = ['red', 'blue', 'grass', 'accent', 'purple', 'azure', 'salmon', 'electro'];

export const LeaderboardWidget: React.FC<LeaderboardWidgetProps> = ({
  title = 'Tournament',
  subtitle,
  img,
  entries = [],
  maxItems = 8,
  variant = 'minimal',
  linkTo,
  className
}) => {
  const { t } = useTranslation('tournament');
  const { t: tCommon } = useTranslation('common');
  // Default to LTR direction (RTL support can be added later if needed)
  const direction = 'ltr';

  // Sort by points and limit entries
  const tableData = [...entries]
    .sort((a, b) => (b.pts ?? b.total_points ?? 0) - (a.pts ?? a.total_points ?? 0))
    .slice(0, maxItems);

  // Transform entries to LeaderboardRowData format
  const rowData: LeaderboardRowData[] = tableData.map((entry, index) => ({
    name: entry.name || `${entry.first_name || ''} ${entry.last_name || ''}`.trim() || entry.username || 'Unknown',
    color: entry.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
    pts: entry.pts ?? entry.total_points ?? 0,
    score: entry.pts ?? entry.total_points ?? 0,
    w: entry.w ?? 0,
    d: entry.d ?? 0,
    l: entry.l ?? 0
  }));

  const content = (
    <Spring className={classNames('card d-flex flex-column g-20 card-padded h-100', className)}>
      <LeaderboardHeader
        title={title}
        subtitle={subtitle}
        img={img}
        variant="compact"
      />
      <div className="d-flex flex-column g-4 flex-1">
        {/* Table Header */}
        <div className={classNames(styles.tableHeader, styles[direction], 'label h6')}>
          <span className="flex-1">
            {variant === 'league' ? t('club') : t('player')}
          </span>
          {variant === 'league' && (
            <div className={styles.headerPoints}>
              <span>{t('team1Win')}</span>
              <span>{t('drawLabel')}</span>
              <span>{t('team2Win')}</span>
            </div>
          )}
          <span>{tCommon('pts')}</span>
        </div>

        {/* Table Body */}
        <div className="d-flex flex-column g-1 flex-1">
          {rowData.length > 0 ? (
            rowData.map((item, index) => (
              <LeaderboardRow
                key={index}
                data={item}
                index={index}
                variant={variant}
              />
            ))
          ) : (
            <div className={styles.empty}>{tCommon('noDataYet')}</div>
          )}
        </div>
      </div>
    </Spring>
  );

  // Wrap in Link if linkTo is provided
  if (linkTo) {
    return (
      <Link to={linkTo} className={styles.link}>
        {content}
      </Link>
    );
  }

  return content;
};

export default LeaderboardWidget;
